/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { examApi, submissionApi, imageApi } from '../services/api';
import PreviewImageModal from '../components/PreviewImageModal';
import { useAuth } from '../context/AuthContext';
import { Exam, SubmitAnswer } from '../types';

// Ensure KaTeX CSS is present so math from QuestionEditor renders correctly in student view
const ensureKatexCss = () => {
    if (document.getElementById('katex-css')) return;
    const link = document.createElement('link');
    link.id = 'katex-css';
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.10/dist/katex.min.css';
    document.head.appendChild(link);
};

const TakeExam: React.FC = () => {
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);
    const { examId } = useParams<{ examId: string }>();
    const [exam, setExam] = useState<Exam | null>(null);
    const [answers, setAnswers] = useState<Map<number, SubmitAnswer>>(new Map());
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        ensureKatexCss();
    }, []);

    useEffect(() => {
        const fetchExam = async () => {
            try {
                const data = await examApi.getById(Number(examId));
                setExam(data);
                const durationSeconds = (data.durationMinutes ?? 30) * 60;
                setTimeLeft(durationSeconds);
            } catch (error) {
                console.error('Error fetching exam:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchExam();
    }, [examId]);

    useEffect(() => {
        if (timeLeft <= 0) return;

        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleSubmit();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeLeft > 0]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    const handleMCQAnswer = (questionId: number, answerId: number) => {
        const newAnswers = new Map(answers);
        newAnswers.set(questionId, { questionId, selectedAnswerId: answerId });
        setAnswers(newAnswers);
    };

    const handleSubjectiveAnswer = (questionId: number, text: string) => {
        const prev = answers.get(questionId);
        const newAnswers = new Map(answers);
        newAnswers.set(questionId, { questionId, textAnswer: text, answerImagePaths: prev?.answerImagePaths });
        setAnswers(newAnswers);
    };

    const setAnswerPageAtIndex = (questionId: number, index: number, url: string | null) => {
        const current = answers.get(questionId) || { questionId } as SubmitAnswer;
        const pages = [...(current.answerImagePaths || [null, null, null])];
        // Ensure length 3
        while (pages.length < 3) pages.push(null);
        pages[index] = url ?? undefined as any;
        const normalized = pages
            .slice(0, 3)
            .map((p) => (p === null ? undefined : p)) as (string | undefined)[];
        // Trim trailing undefined to keep payload small but preserve order of defined pages
        let trimmed = [...normalized] as string[];
        while (trimmed.length > 0 && (trimmed[trimmed.length - 1] == null)) {
            trimmed.pop();
        }
        const updated: SubmitAnswer = {
            questionId,
            selectedAnswerId: current.selectedAnswerId,
            textAnswer: current.textAnswer,
            answerImagePaths: trimmed.filter((x) => typeof x === 'string') as string[],
        };
        const newAnswers = new Map(answers);
        newAnswers.set(questionId, updated);
        setAnswers(newAnswers);
    };

    const uploadAnswerPage = async (questionId: number, index: number, file: File) => {
        try {
            const { url } = await imageApi.uploadAnswer(file, 'button');
            setAnswerPageAtIndex(questionId, index, url);
        } catch (e: any) {
            alert(e?.message || 'Failed to upload page. Please try again.');
        }
    };

    const removeAnswerPage = (questionId: number, index: number) => {
        setAnswerPageAtIndex(questionId, index, null);
    };

    const handleSubmit = async () => {
        if (!user || !exam) return;

        setSubmitting(true);
        try {
            const submission = await submissionApi.submit(
                {
                    examId: exam.id!,
                    answers: Array.from(answers.values()),
                },
                user.id
            );
            navigate(`/result/${submission.id}`);
        } catch (error) {
            console.error('Error submitting exam', error);
            alert('Failed to submit exam. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!exam) {
        return (
            <div className="empty-state">
                <div className="empty-state-emoji">😕</div>
                <h3 className="empty-state-title">Exam Not Found</h3>
            </div>
        );
    }

    return (
        <div data-testid="take-exam">
            {/* Timer Bar*/}
            <div
                className="card"
                style={{
                    position: 'sticky',
                    top: '80px',
                    zIndex: 10,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: timeLeft < 60 ? 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)' : 'white',
                    color: timeLeft < 60 ? 'white' : 'inherit',
                }}
            >
                <div>
                    <h2 style={{ margin: 0 }}>{exam.title}</h2>
                    <p style={{ margin: 0, opacity: 0.8 }}>Total Marks: {exam.totalMarks}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>⏰ Time Remaining</div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{formatTime(timeLeft)}</div>
                </div>
            </div>

            {/* Progress Bar  */}
            <div className="progress-bar">
                <div
                    className="progress-fill"
                    style={{
                        width: `${(answers.size / (exam.questions?.length || 1)) * 100}%`,
                    }}
                ></div>
            </div>
            <p style={{ textAlign: 'center', color: '#718096', marginBottom: '2rem' }}>
                {answers.size} of {exam.questions?.length} questions answered
            </p>

            {/* Questions */}
            {exam.questions?.map((question, index) => (
                <div
                    key={question.id}
                    className={`question-card ${question.questionType.toLowerCase()}`}
                    data-testid={`question-${question.id}`}
                >
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                        <span className="question-number">{index + 1}</span>
                        <div style={{ flex: 1 }}>
                            <div className="question-marks">⭐ {question.marks} marks</div>
                            {/* Primary content: image or text */}
                            {question.imageAsPrimary && question.imagePath ? (
                                <div style={{ marginBottom: '0.75rem' }}>
                                    <img src={question.imagePath} alt={`Question ${index + 1}`} style={{ maxWidth: '100%', borderRadius: 8 }} />
                                </div>
                            ) : (
                                <>
                                    {question.questionRichText && question.questionRichText.trim().length > 0 ? (
                                        <div className="question-text" style={{ marginBottom: '0.5rem' }} dangerouslySetInnerHTML={{ __html: question.questionRichText }} />
                                    ) : (
                                        <p className="question-text" style={{ marginBottom: '0.5rem' }}>{question.questionText}</p>
                                    )}
                                </>
                            )}

                            {/* Reference image (if not primary) */}
                            {!question.imageAsPrimary && question.imagePath && (
                                <div style={{ marginBottom: '0.75rem' }}>
                                    <img src={question.imagePath} alt={`Reference for question ${index + 1}`} style={{ maxWidth: '100%', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                                </div>
                            )}

                            {/* Shapes Rendering */}
                            {question.shapesJson && question.shapesJson.trim().length > 0 && (
                                <div style={{ marginTop: '0.5rem' }}>
                                    <svg viewBox="0 0 100 80" width="100%" height="160">
                                        {(JSON.parse(question.shapesJson || '[]') as any[]).map((s, i) => {
                                            if (s.type === 'circle') return <circle key={i} cx={s.cx} cy={s.cy} r={s.r} stroke={s.stroke} fill={s.fill} />;
                                            if (s.type === 'rect') return <rect key={i} x={s.x} y={s.y} width={s.width} height={s.height} stroke={s.stroke} fill={s.fill} />;
                                            if (s.type === 'square') return <rect key={i} x={s.x} y={s.y} width={s.size} height={s.size} stroke={s.stroke} fill={s.fill} />;
                                            if (s.type === 'trapezium') return <polygon key={i} points={s.points} stroke={s.stroke} fill={s.fill} />;
                                            return null;
                                        })}
                                    </svg>
                                </div>
                            )}
                        </div>
                    </div>

                    {question.questionType === 'MCQ' && question.answers && (
                        <div className="answer-options">
                            {question.answers.map((answer) => (
                                <div
                                    key={answer.id}
                                    className={`answer-option ${
                                        answers.get(question.id!)?.selectedAnswerId === answer.id ? 'selected' : ''
                                        }`}
                                    onClick={() => handleMCQAnswer(question.id!, answer.id!)}
                                    data-testid={`answer-${answer.id}`}
                                >
                                    <div className="answer-radio"></div>
                                    <span>{answer.answerText}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {question.questionType === 'SUBJECTIVE' && (
                        <>
                            <textarea
                                className="form-input"
                                placeholder="✍ Write your answer here..."
                                value={answers.get(question.id!)?.textAnswer || ''}
                                onChange={(e) => handleSubjectiveAnswer(question.id!, e.target.value)}
                                data-testid={`subjective-input-${question.id}`}
                            />

                            {/* Subjective answer pages (max 3) */}
                            <div className="card" style={{ marginTop: '0.75rem' }}>
                                <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div>
                                        <strong>Attach up to 3 pages</strong>
                                        <div style={{ fontSize: 12, color: '#718096' }}>First upload should be Page 1, then Page 2, then Page 3. You can replace or remove any page before submitting.</div>
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                                    {[0,1,2].map((slot) => {
                                        const current = answers.get(question.id!);
                                        const pageUrl = current?.answerImagePaths && current.answerImagePaths.length > slot ? current.answerImagePaths[slot] : undefined;
                                        const inputId = `file-input-q${question.id}-p${slot+1}`;
                                        return (
                                            <div key={slot} className="card" style={{ padding: 12, textAlign: 'center' }}>
                                                <div style={{ fontWeight: 600, marginBottom: 8 }}>Page {slot + 1}</div>
                                                {pageUrl ? (
                                                    <img src={pageUrl} alt={`Answer page ${slot+1}`} style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6, border: '1px solid #e2e8f0', objectFit: 'contain' }} />
                                                ) : (
                                                    <div style={{ height: 160, border: '1px dashed #cbd5e0', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#718096' }}>
                                                        No file
                                                    </div>
                                                )}
                                                <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'center' }}>
                                                    <input
                                                        id={inputId}
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => {
                                                            const file = e.target.files && e.target.files[0];
                                                            if (file) {
                                                                uploadAnswerPage(question.id!, slot, file);
                                                                // Reset input so selecting the same file again triggers change
                                                                e.currentTarget.value = '';
                                                            }
                                                        }}
                                                    />
                                                    <label htmlFor={inputId} className="btn btn-primary btn-sm">
                                                        {pageUrl ? 'Replace' : 'Upload'}
                                                    </label>
                                                    {pageUrl && (
                                                        <>
                                                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPreviewSrc(pageUrl)}>
                                                                Preview
                                                            </button>
                                                            <button type="button" className="btn btn-danger btn-sm" onClick={() => removeAnswerPage(question.id!, slot)}>
                                                                Remove
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ))}

            {/* Submit Button */}
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <button
                    className="btn btn-success btn-lg"
                    onClick={handleSubmit}
                    disabled={submitting}
                    data-testid="submit-button"
                >
                    {submitting ? '⌛ Submitting...' : '✅ Submit Exam'}
                </button>
            </div>
            <PreviewImageModal src={previewSrc || undefined} open={!!previewSrc} onClose={() => setPreviewSrc(null)} alt="Answer page preview" />
        </div>
    );
};

export default TakeExam;
